import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Check,
  ChevronDown,
  ChevronRight,
  Ellipsis,
  Folder,
  FolderPlus,
  GripVertical,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import type { Conversation, User } from "@lumen/shared";
import { api } from "../../api";
import { projectConversationPath } from "./projectViewPreference";
import { t } from "../../i18n";
import { ConfirmDangerDialog } from "../shared/ConfirmDangerDialog";
import { ThemeSwitch } from "../ThemeSwitch";
import { SidebarFooter } from "../shared/SidebarFooter";
import { ConversationStatusIndicator } from "../shared/ConversationStatusIndicator";
import {
  conversationTitleChangedEvent,
  knownConversationTitle,
  rememberConversationTitles,
} from "../../conversationTitles";
import {
  PROJECTS_CHANGED_EVENT,
  subscribeToDataChanges,
} from "./projectEvents";
import {
  canEditProject,
  mergeProjectExpansionState,
  normalizeProjectRows,
  type ProjectWithConversations,
} from "./projectHelpers";

export function ProjectSidebar({
  user,
  setOpen,
}: {
  user: User;
  setOpen: (value: boolean) => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectWithConversations[]>([]);
  const [, setConversationTitleRevision] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [conversationLimits, setConversationLimits] = useState<
    Record<string, number>
  >({});
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [conversationMenuId, setConversationMenuId] = useState<string | null>(
    null,
  );
  const [conversationMenuView, setConversationMenuView] = useState<
    "root" | "projects"
  >("root");
  const [conversationDeleteTarget, setConversationDeleteTarget] =
    useState<Conversation | null>(null);
  const [draggedProjectId, setDraggedProjectId] = useState<string | null>(null);
  const [dragOverProjectId, setDragOverProjectId] = useState<string | null>(
    null,
  );
  const [deleteTarget, setDeleteTarget] =
    useState<ProjectWithConversations | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const loadInFlight = useRef(false);
  const loadQueued = useRef(false);
  const loadRevision = useRef(0);
  const orderSaveChain = useRef(Promise.resolve());
  const expandedSaveChains = useRef(new Map<string, Promise<void>>());
  const pendingExpanded = useRef(new Map<string, boolean>());
  const expandedRequestIds = useRef(new Map<string, number>());
  const nextExpandedRequestId = useRef(0);
  const loadedContextKey = useRef<string | null>(null);
  const sidebarInstanceId = useRef(
    `project-sidebar-${Math.random().toString(36).slice(2)}`,
  ).current;
  const userContextKey = `${user.id}:${[...user.organizationPermissions].sort().join(",")}`;

  useEffect(() => {
    const refreshTitles = () =>
      setConversationTitleRevision((value) => value + 1);
    window.addEventListener(conversationTitleChangedEvent, refreshTitles);
    return () =>
      window.removeEventListener(conversationTitleChangedEvent, refreshTitles);
  }, []);

  const load = useCallback(async () => {
    if (loadInFlight.current) {
      loadQueued.current = true;
      return;
    }
    const revision = ++loadRevision.current;
    loadInFlight.current = true;
    try {
      const rows = await api<ProjectWithConversations[]>("/projects", {
        cache: "no-store",
      });
      if (revision !== loadRevision.current) return;
      const nextProjects = normalizeProjectRows(rows);
      rememberConversationTitles(
        nextProjects.flatMap((project) => project.conversations),
      );
      setProjects(nextProjects);
      setExpanded(() =>
        mergeProjectExpansionState(nextProjects, pendingExpanded.current),
      );
      setConversationLimits((current) =>
        Object.fromEntries(
          Object.entries(current).filter(([projectId]) =>
            nextProjects.some((project) => project.id === projectId),
          ),
        ),
      );
      setError("");
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : t("Projects could not be loaded."),
      );
    } finally {
      loadInFlight.current = false;
      if (loadQueued.current) {
        loadQueued.current = false;
        void load();
      }
    }
  }, []);

  useEffect(() => {
    if (!openMenuId && !conversationMenuId) return;
    const closeOnOutsideClick = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Element) {
        if (
          !target.closest("[data-project-menu]") &&
          !target.closest("[data-conversation-menu]")
        ) {
          setOpenMenuId(null);
          setConversationMenuId(null);
        }
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpenMenuId(null);
        setConversationMenuId(null);
      }
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [openMenuId, conversationMenuId]);

  const persistExpanded = async (projectId: string, nextExpanded: boolean) => {
    const requestId = ++nextExpandedRequestId.current;
    pendingExpanded.current.set(projectId, nextExpanded);
    expandedRequestIds.current.set(projectId, requestId);
    setExpanded((current) => {
      const next = new Set(current);
      if (nextExpanded) next.add(projectId);
      else next.delete(projectId);
      return next;
    });
    const previousSave =
      expandedSaveChains.current.get(projectId) ?? Promise.resolve();
    const save = previousSave
      .catch(() => undefined)
      .then(async () => {
        try {
          await api(`/projects/${projectId}/view`, {
            method: "PATCH",
            body: JSON.stringify({ expanded: nextExpanded }),
            notifyDataChange: false,
          });
          if (expandedRequestIds.current.get(projectId) === requestId) {
            pendingExpanded.current.delete(projectId);
            expandedRequestIds.current.delete(projectId);
          }
        } catch (reason) {
          if (expandedRequestIds.current.get(projectId) !== requestId) return;
          pendingExpanded.current.delete(projectId);
          expandedRequestIds.current.delete(projectId);
          setExpanded((current) => {
            if (current.has(projectId) !== nextExpanded) return current;
            const next = new Set(current);
            if (nextExpanded) next.delete(projectId);
            else next.add(projectId);
            return next;
          });
          setError(
            reason instanceof Error
              ? reason.message
              : t("Project view could not be saved."),
          );
        }
      });
    const trackedSave = save.finally(() => {
      if (expandedSaveChains.current.get(projectId) === trackedSave)
        expandedSaveChains.current.delete(projectId);
    });
    expandedSaveChains.current.set(projectId, trackedSave);
    await save;
  };

  const saveProjectOrder = (nextProjects: ProjectWithConversations[]) => {
    const projectIds = nextProjects.map((project) => project.id);
    orderSaveChain.current = orderSaveChain.current
      .catch(() => undefined)
      .then(async () => {
        await api("/projects/order", {
          method: "PUT",
          body: JSON.stringify({ projectIds }),
        });
      })
      .catch((reason) => {
        setError(
          reason instanceof Error
            ? reason.message
            : t("Project order could not be saved."),
        );
        void load();
      });
  };

  const reorderProjects = (projectId: string, targetIndex: number) => {
    const fromIndex = projects.findIndex((project) => project.id === projectId);
    if (fromIndex < 0 || fromIndex === targetIndex) return;
    const nextProjects = [...projects];
    const [project] = nextProjects.splice(fromIndex, 1);
    nextProjects.splice(targetIndex, 0, project);
    setProjects(nextProjects);
    saveProjectOrder(nextProjects);
  };

  const moveProject = (projectId: string, direction: -1 | 1) => {
    const index = projects.findIndex((project) => project.id === projectId);
    const targetIndex = index + direction;
    if (index < 0 || targetIndex < 0 || targetIndex >= projects.length) return;
    reorderProjects(projectId, targetIndex);
    setOpenMenuId(null);
  };

  const markConversationSeen = useCallback(
    async (conversation: Conversation) => {
      if (conversation.attention !== "response-ready") return;
      try {
        await api(`/conversations/${conversation.id}/seen`, {
          method: "POST",
          notifyDataChange: false,
        });
        setProjects((current) =>
          current.map((project) => ({
            ...project,
            conversations: project.conversations.map((item) =>
              item.id === conversation.id
                ? { ...item, attention: null, attentionMessageId: null }
                : item,
            ),
          })),
        );
      } catch {
        // The next project refresh will reconcile an unsuccessful seen request.
      }
    },
    [],
  );

  useEffect(() => {
    const selectedId = location.pathname.match(/^\/projects\/c\/([^/]+)$/)?.[1];
    if (!selectedId) return;
    const selected = projects
      .flatMap((project) => project.conversations)
      .find((conversation) => conversation.id === selectedId);
    if (selected) void markConversationSeen(selected);
  }, [location.pathname, markConversationSeen, projects]);

  useEffect(() => {
    loadRevision.current += 1;
    if (
      loadedContextKey.current !== null &&
      loadedContextKey.current !== userContextKey
    ) {
      setProjects([]);
      setExpanded(new Set());
      setConversationLimits({});
      pendingExpanded.current.clear();
      expandedRequestIds.current.clear();
    }
    loadedContextKey.current = userContextKey;
    void load();
    const refreshIfVisible = () => {
      if (document.visibilityState === "visible") void load();
    };
    window.addEventListener("focus", refreshIfVisible);
    window.addEventListener("online", refreshIfVisible);
    document.addEventListener("visibilitychange", refreshIfVisible);
    const unsubscribeFromDataChanges = subscribeToDataChanges(refreshIfVisible);
    return () => {
      window.removeEventListener("focus", refreshIfVisible);
      window.removeEventListener("online", refreshIfVisible);
      document.removeEventListener("visibilitychange", refreshIfVisible);
      unsubscribeFromDataChanges();
    };
  }, [load, userContextKey]);

  // Like the Chat view, a project conversation only exists once its first
  // message is sent. The "+" button opens a draft composer scoped to the
  // project; ChatPage creates the conversation when the message is submitted.
  const startProjectConversation = (project: ProjectWithConversations) => {
    setError("");
    void persistExpanded(project.id, true);
    navigate(`/projects/conversations/new?project=${project.id}`);
    setOpen(false);
  };

  const deleteProject = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await api(`/projects/${deleteTarget.id}`, { method: "DELETE" });
      setProjects((current) =>
        current.filter((project) => project.id !== deleteTarget.id),
      );
      window.dispatchEvent(new Event(PROJECTS_CHANGED_EVENT));
      setDeleteTarget(null);
      if (location.pathname.startsWith(`/projects/edit/${deleteTarget.id}`))
        navigate("/projects");
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : t("Could not delete project."),
      );
    } finally {
      setBusy(false);
    }
  };

  const moveProjectConversationToChat = (conversation: Conversation) => {
    setConversationMenuId(null);
    // Moving to Chat clears the project group; the Project view refreshes on
    // the data-change event and the Chat view reconciles on its own poll.
    void api<Conversation>(`/conversations/${conversation.id}`, {
      method: "PATCH",
      body: JSON.stringify({ surface: "chat", groupId: null }),
    })
      .then(() => {
        void load();
        if (location.pathname === `/projects/c/${conversation.id}`)
          navigate("/", { replace: true });
      })
      .catch((reason) => {
        setError(
          reason instanceof Error ? reason.message : t("Could not move chat."),
        );
      });
  };

  const moveProjectConversationToProject = (
    conversation: Conversation,
    targetProjectId: string,
  ) => {
    setConversationMenuId(null);
    void api<Conversation>(`/conversations/${conversation.id}`, {
      method: "PATCH",
      body: JSON.stringify({ surface: "project", groupId: targetProjectId }),
    })
      .then(() => {
        void load();
        if (location.pathname === `/projects/c/${conversation.id}`)
          navigate(`/projects/c/${conversation.id}`, { replace: true });
      })
      .catch((reason) => {
        setError(
          reason instanceof Error ? reason.message : t("Could not move chat."),
        );
      });
  };

  const deleteProjectConversation = async () => {
    const target = conversationDeleteTarget;
    if (!target) return;
    setBusy(true);
    try {
      await api(`/conversations/${target.id}`, {
        method: "DELETE",
      });
      setProjects((current) =>
        current.map((project) => ({
          ...project,
          conversations: project.conversations.filter(
            (item) => item.id !== target.id,
          ),
        })),
      );
      window.dispatchEvent(new Event(PROJECTS_CHANGED_EVENT));
      setConversationDeleteTarget(null);
      if (location.pathname === `/projects/c/${target.id}`)
        navigate("/projects");
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : t("Could not delete conversation."),
      );
    } finally {
      setBusy(false);
    }
  };

  const activeProject = projects.find((project) =>
    project.conversations.some(
      (conversation) =>
        location.pathname === `/projects/c/${conversation.id}` ||
        location.pathname === `/projects/c/${conversation.id}/workspace`,
    ),
  );
  const activeView = /\/workspace\/?$/.test(location.pathname)
    ? "editor"
    : "chat";

  const conversationLink = (conversation: Conversation) => {
    const selected =
      location.pathname === `/projects/c/${conversation.id}` ||
      location.pathname === `/projects/c/${conversation.id}/workspace`;
    const showAttention =
      conversation.attention === "action-required" ||
      (conversation.attention === "response-ready" && !selected);
    return (
      <div
        key={conversation.id}
        data-conversation-attention={
          showAttention ? conversation.attention : undefined
        }
        className={`group flex items-center rounded-lg ${selected ? "bg-white font-medium text-lumen-800 shadow-sm dark:bg-white/10 dark:text-lumen-200" : "text-stone-600 hover:bg-white/70 dark:text-stone-400 dark:hover:bg-white/5"}`}
      >
        <Link
          to={projectConversationPath(
            conversation,
            activeProject?.id === conversation.groupId ? activeView : undefined,
          )}
          draggable={false}
          onClick={() => {
            void markConversationSeen(conversation);
            setOpen(false);
          }}
          className="min-w-0 flex-1 truncate rounded-l-lg px-3 py-2 text-sm"
        >
          {conversation.title === "New conversation"
            ? (knownConversationTitle(conversation.id) ?? conversation.title)
            : conversation.title}
        </Link>
        <ConversationStatusIndicator
          conversation={conversation}
          selected={selected}
        />
        <div className="relative" data-conversation-menu>
          <button
            type="button"
            id={`project-conversation-menu-${conversation.id}`}
            onClick={() => {
              setConversationMenuView("root");
              setConversationMenuId((current) =>
                current === conversation.id ? null : conversation.id,
              );
            }}
            aria-haspopup="menu"
            aria-expanded={conversationMenuId === conversation.id}
            className={`mr-1 rounded-lg p-1.5 transition hover:bg-white hover:text-lumen-700 focus-visible:opacity-100 dark:hover:bg-white/10 dark:hover:text-lumen-300 ${selected ? "opacity-100" : "opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 pointer-coarse:opacity-100"} text-stone-400`}
            title={t("Conversation actions")}
            aria-label={t("Conversation actions")}
          >
            <Ellipsis size={14} />
          </button>
          {conversationMenuId === conversation.id && (
            <div
              role="menu"
              className="absolute top-full right-0 z-20 mt-1 min-w-48 rounded-xl border border-stone-200 bg-white p-1 shadow-xl dark:border-white/10 dark:bg-stone-900"
            >
              {conversationMenuView === "root" ? (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-stone-600 hover:bg-stone-100 hover:text-lumen-700 dark:text-stone-300 dark:hover:bg-white/10"
                    onClick={() => moveProjectConversationToChat(conversation)}
                  >
                    <MessageSquare size={14} /> {t("Move to Chat")}
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-stone-600 hover:bg-stone-100 hover:text-lumen-700 dark:text-stone-300 dark:hover:bg-white/10"
                    onClick={() => setConversationMenuView("projects")}
                  >
                    <Folder size={14} /> {t("Move into project")}
                    <ChevronRight
                      size={13}
                      className="ml-auto shrink-0 text-stone-400"
                    />
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                    onClick={() => {
                      setConversationDeleteTarget(conversation);
                      setConversationMenuId(null);
                    }}
                  >
                    <Trash2 size={14} /> {t("Delete")}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-stone-600 hover:bg-stone-100 hover:text-lumen-700 dark:text-stone-300 dark:hover:bg-white/10"
                    onClick={() => setConversationMenuView("root")}
                  >
                    <ArrowLeft size={14} /> {t("Back")}
                  </button>
                  <div className="my-1 border-t border-stone-100 dark:border-white/10" />
                  {projects.length === 0 && (
                    <p className="px-2 py-1.5 text-xs text-stone-400">
                      {t("No projects yet.")}
                    </p>
                  )}
                  {projects.length > 0 &&
                    projects.filter(
                      (project) => project.id !== conversation.groupId,
                    ).length === 0 && (
                      <p className="px-2 py-1.5 text-xs text-stone-400">
                        {t("This conversation is already in this project.")}
                      </p>
                    )}
                  {projects
                    .filter((project) => project.id !== conversation.groupId)
                    .map((project) => (
                      <button
                        key={project.id}
                        type="button"
                        role="menuitem"
                        className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-stone-600 hover:bg-stone-100 hover:text-lumen-700 dark:text-stone-300 dark:hover:bg-white/10"
                        onClick={() =>
                          moveProjectConversationToProject(
                            conversation,
                            project.id,
                          )
                        }
                      >
                        <Folder
                          size={14}
                          className="shrink-0 text-lumen-600 dark:text-lumen-300"
                        />
                        <span className="min-w-0 flex-1 truncate">
                          {project.name}
                        </span>
                        {conversation.groupId === project.id && (
                          <Check
                            size={14}
                            className="shrink-0 text-lumen-600 dark:text-lumen-300"
                          />
                        )}
                      </button>
                    ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <aside
      data-sidebar-instance={sidebarInstanceId}
      className="flex size-full min-h-0 flex-col border-r border-stone-200 bg-[#f4f7f5] p-3 sm:w-72 dark:border-white/10 dark:bg-[#121615]"
    >
      <div className="flex h-12 items-center justify-between px-2">
        <div>
          <div className="text-xs font-bold tracking-[.18em] text-lumen-600 uppercase">
            {t("Projects")}
          </div>
          <div className="text-lg font-bold tracking-tight">
            {t("Project view")}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <ThemeSwitch />
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="button p-2 lg:hidden"
            aria-label={t("Close sidebar")}
          >
            <X size={18} />
          </button>
        </div>
      </div>
      <Link
        to="/projects/new"
        onClick={() => setOpen(false)}
        className="button-primary mt-4 justify-start"
      >
        <FolderPlus size={17} /> {t("Create new project")}
      </Link>
      <nav
        data-testid="sidebar-conversations"
        className="mt-4 min-h-0 flex-1 space-y-2 overflow-y-auto"
      >
        {projects.map((project) => {
          const isExpanded = expanded.has(project.id);
          const visibleConversationCount = conversationLimits[project.id] ?? 5;
          const conversations = Array.isArray(project.conversations)
            ? project.conversations
            : [];
          const visibleConversations = conversations.slice(
            0,
            visibleConversationCount,
          );
          const canEdit = canEditProject(project, user);
          return (
            <section
              key={project.id}
              data-project-id={project.id}
              draggable
              onDragStart={(event) => {
                event.dataTransfer.effectAllowed = "move";
                setDraggedProjectId(project.id);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                if (draggedProjectId && draggedProjectId !== project.id)
                  setDragOverProjectId(project.id);
              }}
              onDragLeave={() => setDragOverProjectId(null)}
              onDrop={(event) => {
                event.preventDefault();
                if (draggedProjectId && draggedProjectId !== project.id) {
                  const targetIndex = projects.findIndex(
                    (item) => item.id === project.id,
                  );
                  reorderProjects(draggedProjectId, targetIndex);
                }
                setDraggedProjectId(null);
                setDragOverProjectId(null);
              }}
              onDragEnd={() => {
                setDraggedProjectId(null);
                setDragOverProjectId(null);
              }}
              className="rounded-xl border border-stone-200 bg-white/50 p-1 dark:border-white/10 dark:bg-white/3"
            >
              <div
                className={`group relative flex items-center gap-1 rounded-lg p-1 ${dragOverProjectId === project.id ? "ring-2 ring-lumen-400" : ""}`}
              >
                <span title={t("Drag to reorder")}>
                  <GripVertical
                    size={14}
                    className="shrink-0 cursor-grab text-stone-300 active:cursor-grabbing dark:text-stone-600"
                  />
                </span>
                <button
                  type="button"
                  aria-expanded={isExpanded}
                  className="flex min-w-0 flex-1 items-center gap-1.5 rounded-lg p-1 text-left text-sm font-semibold text-stone-600 hover:text-lumen-700 dark:text-stone-300 dark:hover:text-lumen-300"
                  onClick={() => void persistExpanded(project.id, !isExpanded)}
                >
                  <ChevronDown
                    size={14}
                    className={`shrink-0 transition-transform ${isExpanded ? "" : "-rotate-90"}`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{project.name}</span>
                    <span className="block truncate text-[11px] font-normal text-stone-400">
                      {project.visibility === "organization"
                        ? t("Organization project")
                        : project.description || t("Private project")}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  className="rounded-lg p-1 text-stone-400 hover:text-lumen-700"
                  onClick={() => startProjectConversation(project)}
                  title={t("New project conversation")}
                  aria-label={t("New project conversation")}
                >
                  <Plus size={14} />
                </button>
                <div className="relative" data-project-menu>
                  <button
                    type="button"
                    className="rounded-lg p-1 text-stone-400 hover:bg-white hover:text-lumen-700 dark:hover:bg-white/10"
                    onClick={() =>
                      setOpenMenuId((current) =>
                        current === project.id ? null : project.id,
                      )
                    }
                    title={t("Project actions")}
                    aria-label={t("Project actions")}
                    aria-expanded={openMenuId === project.id}
                    aria-haspopup="menu"
                  >
                    <MoreHorizontal size={15} />
                  </button>
                  {openMenuId === project.id && (
                    <div
                      role="menu"
                      className="absolute top-full right-0 z-20 mt-1 min-w-44 rounded-xl border border-stone-200 bg-white p-1 shadow-xl dark:border-white/10 dark:bg-stone-900"
                    >
                      {canEdit && (
                        <>
                          <Link
                            to={`/projects/edit/${project.id}`}
                            draggable={false}
                            role="menuitem"
                            onClick={() => setOpenMenuId(null)}
                            className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-stone-600 hover:bg-stone-100 hover:text-lumen-700 dark:text-stone-300 dark:hover:bg-white/10"
                          >
                            <Pencil size={14} /> {t("Edit project")}
                          </Link>
                          <button
                            type="button"
                            role="menuitem"
                            className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                            onClick={() => {
                              setDeleteTarget(project);
                              setOpenMenuId(null);
                            }}
                          >
                            <Trash2 size={14} /> {t("Delete project")}
                          </button>
                          <div className="my-1 border-t border-stone-100 dark:border-white/10" />
                        </>
                      )}
                      <button
                        type="button"
                        role="menuitem"
                        disabled={projects[0]?.id === project.id}
                        className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-stone-600 hover:bg-stone-100 disabled:opacity-40 dark:text-stone-300 dark:hover:bg-white/10"
                        onClick={() => moveProject(project.id, -1)}
                      >
                        <ArrowUp size={14} /> {t("Move up")}
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        disabled={
                          projects[projects.length - 1]?.id === project.id
                        }
                        className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-stone-600 hover:bg-stone-100 disabled:opacity-40 dark:text-stone-300 dark:hover:bg-white/10"
                        onClick={() => moveProject(project.id, 1)}
                      >
                        <ArrowDown size={14} /> {t("Move down")}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {isExpanded && (
                <div className="space-y-1 px-1 pb-1">
                  {visibleConversations.map(conversationLink)}
                  {conversations.length > visibleConversationCount && (
                    <button
                      type="button"
                      className="w-full rounded-lg px-3 py-2 text-left text-xs font-medium text-lumen-700 hover:bg-white/70 dark:text-lumen-300 dark:hover:bg-white/5"
                      onClick={() =>
                        setConversationLimits((current) => ({
                          ...current,
                          [project.id]: visibleConversationCount + 10,
                        }))
                      }
                    >
                      {t("Show more")}
                    </button>
                  )}
                  {!conversations.length && (
                    <p className="px-3 py-2 text-xs text-stone-400">
                      {t("No conversations yet.")}
                    </p>
                  )}
                </div>
              )}
            </section>
          );
        })}
        {!projects.length && !error && (
          <p className="p-3 text-center text-sm text-stone-400">
            {t("Create a project to organize implementation conversations.")}
          </p>
        )}
        {error && (
          <p className="p-3 text-xs text-red-600 dark:text-red-300">{error}</p>
        )}
      </nav>
      <SidebarFooter user={user} view="project" setOpen={setOpen} />
      {deleteTarget && (
        <ConfirmDangerDialog
          title={t("Delete project?")}
          description={t(
            "Delete this project? Its conversations can be moved to Chat first, or will be removed when the project is deleted.",
          )}
          confirmLabel={t("Delete project")}
          busy={busy}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => void deleteProject()}
        />
      )}
      {conversationDeleteTarget && (
        <ConfirmDangerDialog
          title={t("Delete conversation?")}
          description={t(
            "This permanently deletes the project conversation, all message versions, and any active share link.",
          )}
          confirmLabel={t("Delete")}
          busy={busy}
          onCancel={() => setConversationDeleteTarget(null)}
          onConfirm={() => void deleteProjectConversation()}
        />
      )}
    </aside>
  );
}
